import swatchMaterial from '@/mixins/Customizer/SwatchMaterial';
import geometryUpdates from '@/mixins/geometryUpdates';
import merchandisingRules from '@/mixins/geometryUpdates/merchandisingRules';
import { getLocale } from "@/data/regionInfo";
import { fetchAndInsertImageToGraphicSettings } from "@/libs/graphics/GraphicImageAssetProcesser"
import exclusivityLogic from '@/mixins/exclusivityLogic';
import undo from "@/mixins/Customizer/Undo";
import finishMyDesign from "@/mixins/Customizer/FinishMyDesign";
import randomize from "@/mixins/Customizer/Randomize";
import GraphicSwatchController from "@/libs/swatch/GraphicSwatchController";
import { decoStitch } from '@/data/constants';

const cloneDeep = require("lodash/cloneDeep");

export default {
  mixins: [
    exclusivityLogic,
    swatchMaterial,
    geometryUpdates,
    merchandisingRules,
    undo,
    finishMyDesign,
    randomize
  ],
  computed: {
    getGdm() {
      return this.$store.state.gdm.data || {};
    },
    isApparel() {
      return this.$store.getters['utils/isApparelEnabled'];
    },
    activeComponent() {
      const {
        activeComponent,
        activeSubComponent,
      } = this.$store.state.standardNavigation;

      if (this.isApparel) {
        return activeSubComponent !== ""
          ? `${activeComponent}/${activeSubComponent}`
          : activeComponent;
      }

      return this.$store.state.legacyNavigation.activeComponent;
    },
    activeComponentIsNotUgc() {
      return !this.$recipe.getUGCFeatureType(this.activeComponent);
    },
    activeComponentIsUGC() {
      return this.$store.state.ugc.tempData.features[this.$store.state.ugc.tempData.activeUGCIndex].activeUGCComponent === this.activeComponent;
    },
    componentsWithGraphicsSupport() {
      return this.$store.getters["utils/componentsWithGraphicsSupport"];
    },
    selectedSwatch() {
      return this.$store.state.swatches.selectedSwatches[this.activeComponent];
    },
    productOptionToggle() {
      return this.$store.state.swatches.productOptionToggle;
    },
    pdm() {
      return this.$store.state.utils.pdm;
    },
    isDecoStichApplied() {
      return this.$store.state.geometryUpdates.decostitchApplied;
    },
    upgradeOptionsStore() {
      return this.$store.state.geometryUpdates.upgradeOptions;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.configuration.enablePerformanceLogging;
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
    isFlyOutMenuEnable() {
      return this.configuration.enableFlyOutMenu;
    },
  },
  methods: {
    returnSwatchName(data) {
      const locale = getLocale();
      return data?.[locale] || data?.default;
    },
    async selectSwatch(swatch, { component = null, applyUndo = false } = {}) {
      const currentComponent = component || this.activeComponent;
      if (this.enablePerformanceLogging)
        console.log(`${currentComponent} Swatch Changed to ${swatch.id}: `, new Date().getTime() / 1000 - this.initialTime);

      const refName = 'swatch-' + swatch.id;
      const swatchName = this.returnSwatchName(swatch.display_name);
      const swatchEl = this.$refs[refName] && this.$refs[refName][0];

      if (swatchEl && swatchEl.classList.contains('disabled')) {
        return false;
      }
      const selectedSwatch = this.$store.state.swatches.selectedSwatches[this.activeComponent];
      if (
        this.recipe_v2.components[this.activeComponent]?.swatch_id === Number(swatch.id)
        && this.activeComponentIsNotUgc
        && (!selectedSwatch || !selectedSwatch.color || selectedSwatch.color === swatch.color)
        && !applyUndo
      ) {
        if (this.isApparel && swatch.material_type === 'graphic') this.removeApparelGraphic(swatch.id);
        return false;
      }

      const swatchGroup = this.$store.getters["swatches/returnSwatchGroupName"]({ swatch });
      const swatchSubgroup = this.$store.getters["swatches/returnSwatchSubgroupName"]({ swatch });
      const swatchUgcSwatchGroup = swatch.ugc_swatch_group || null;
      const externalFunctionParams = {
        currentComponent,
        swatch,
        group: swatchGroup,
        subgroup: swatchSubgroup,
        ugcswatchgroup: swatchUgcSwatchGroup,
      };
      let isLoadingGraphicFromTab = false;

      if (this.isApparel) {
        if (
          // Is a graphics swatch
          this.$store.state.swatches.selectedTabs[this.activeComponent]
          && this.componentsWithGraphicsSupport.indexOf(this.$store.state.swatches.selectedTabs[this.activeComponent]) > -1
        ) {
          isLoadingGraphicFromTab = true;
          // process graphics swatch actions
          if (swatchGroup === 'graphics') {
            this.$eventBus.$emit('navigationActivateUpdatedFlag', false);
          }

          await GraphicSwatchController({
            targetComponent: this.activeComponent,
            swatch,
            swatchGroup,
            swatchSubgroup,
            componentsWithGraphicsSupport: this.componentsWithGraphicsSupport
          })
        }
      }

      this.$eventBus.$emit('rotate-stop', false);
      if (!applyUndo) {
        if( (swatchGroup === 'upgradeoptions' || swatch.self_removable) && swatch?.geometry_updates) {
          const geometryComponent = this.pdm?.merchandising_rules?.swatches[swatch?.geometry_updates?.[0]].activate_components?.[0];
          if(this.recipe_v2?.components?.[geometryComponent]) {
            this.savePreviousSwatch({ feature: false, geometryComponent });
          } else if(this.recipe_v2.components[this.activeComponent]?.personalization) {
            this.savePreviousEmbroidery(this.recipe_v2.components[this.activeComponent]?.personalization, this.activeComponent)
          }
        } else {
          this.savePreviousSwatch({ feature: this.$recipe.getUGCFeatureType(currentComponent) });
        }
      }

      this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'swatches', status: true });
      if (swatchGroup !== 'upgradeoptions' && swatch.material_type !== 'graphic') {
        if (!swatch.ugc) {
          this.isApparel ?
            this.$recipe.removeApparelUGCSwatchfromRecipe(this.activeComponent) :
            this.$recipe.removeUGCSwatchfromRecipe({componentName: currentComponent, componentWithUGCArray: this.$store.getters['ugc/getComponentsWithUGCApplied'], applyUndo, swatch});
          //restore subgroup and group for default swatch and undo
          if (this.recipe_v2.components?.[currentComponent]?.swatch_id === this.$store.state.recipe.defaultRecipe.components?.[currentComponent]?.swatch_id && applyUndo) {
            let data = this.recipe_v2.components[currentComponent];
            data.group = swatchGroup;
            data.subgroup = swatchSubgroup;
            this.$store.commit("recipe/UPDATE_COMPONENT", { componentName: currentComponent, data });
          }
        } else {
          // Validates if the swatch is ugc as swatch and the current component has ugc or patternmaker applied before.
          // If that is true it needs to reset DISABLED_SWATCHES_BY_FEATURE
          // since this swatch has its own exclusive endpoint
          const UGCFeatureType = this.$recipe.getUGCFeatureType(currentComponent);
          if (UGCFeatureType) {
            this.$store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", {
              swatches: [],
              feature: UGCFeatureType
            });
          }
        }
      }

      if (!this.selectedSwatch) {
        this.$store.commit("swatches/SET_HISTORY_SELECTED_TABS", currentComponent);
      }

      if (this.$store.getters['utils/useEcomDataLayerFormat']) {
        this.$customizerDataLayer.logAnalyticsEvent({
          type: this.$customizerDataLayer.types.customizerSwatches,
          eventLabel: swatch.display_name.default,
          swatchGroup: swatchGroup
        });
      } else {
        const event = {
          componentName: currentComponent,
          swatchSubGroupName: swatchSubgroup,
          swatchUgcGroupName: swatchUgcSwatchGroup,
          event: 'trackEvent',
          eventCategory: 'customs',
          eventAction: swatchGroup,
        }
        if(this.configuration.enableSwatchNameTrackEventLabel){
          event.eventLabel = `select: ${swatchName || ''}`;
        }
        dataLayer.push(event);
      }
      if (swatch.geometry_updates && !this.isApparel) {
        // This logic is for the water_bottle geometry && front pocket branding
        if (this.$store.state.geometryUpdates.upgradeOptions[swatch.id] && this.$store.state.geometryUpdates.upgradeOptions[swatch.id].status) {
          for (let objectName of swatch.geometry_updates) {
            const geosToRemove = this.pdm.geometry_updates && this.pdm.geometry_updates.swatches && this.pdm.geometry_updates.swatches[objectName] && this.pdm.geometry_updates.swatches[objectName].remove_geo
            const componentsToDeactivate = this.pdm && this.pdm.merchandising_rules && this.pdm.merchandising_rules.swatches[objectName] && this.pdm.merchandising_rules.swatches[objectName].deactivate_components;
            if (geosToRemove) {
              this.removeGeos({ geosToRemove: this.setGeosToRemove({ geosToRemove }) });
              this.deactivateComponents({ componentsToDeactivate: componentsToDeactivate || geosToRemove, activeComponent: currentComponent, removeFromRecipe: true })
            }
          }

          if (swatch.upgrade_options || swatchGroup === 'upgradeoptions' || swatch.self_removable) {
            this.$store.commit('geometryUpdates/SET_UPGRADE_OPTIONS', {
              swatchId: swatch.id,
              status: false
            });
            if (!applyUndo) {
              this.savePreviousSwatch({ feature: swatch.self_removable ? "selfRemovable" : swatchGroup,  currentSwatch: swatch });
            }
          }
          this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'swatches', status: false });
          return false;
        }
        if (swatch.geometry_updates && swatch.geometry_updates.includes(decoStitch)) {
          this.applyDecoStitch(swatch, currentComponent).then(() => {
            this.updateSwatchesData(externalFunctionParams, applyUndo);
            this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'swatches', status: false });
          });
        } else {
          this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'geostates', status: true });

          if (swatch.upgrade_options || swatchGroup === 'upgradeoptions' || swatch.self_removable) {
            this.$store.commit('geometryUpdates/SET_UPGRADE_OPTIONS', {
              swatchId: swatch.id,
              status: typeof this.$store.state.geometryUpdates.upgradeOptions[swatch.id] !== 'undefined' ? !this.$store.state.geometryUpdates.upgradeOptions[swatch.id].status : true
            })
          }
          this.selfRemoveSwatches({ swatch });
          this.updateModelGeometry({ componentName: currentComponent, swatchObj: swatch })
            .then(() => {
              // This prevents to apply the swatch in the current component
              if (swatchGroup === 'upgradeoptions' || swatch.self_removable) {
               if (!applyUndo && (this.$store.state.undo.previousSwatch.length == 0
                  || this.$store.state.undo.previousSwatch.filter(item => item.feature === "selfRemovable" ).length > 0)) {
                  this.savePreviousSwatch({ feature: "selfRemovable",  currentSwatch: swatch });
                }
                return Promise.resolve()
              } else {
                if (this.activeComponent.search('/') !== -1) {
                  const component = this.activeComponent.split('/');
                  // TODO determine if validateComponentGrouping is needed here (new)
                  return this.applySwatch(this.activeComponent, swatch.id, this.pdm.components[component[0]].component_configuration_group)
                } else {
                  const configGroup = this.validateComponentGrouping({ componentName: currentComponent });
                  return this.applySwatch(currentComponent, swatch.id, configGroup)
                }
              }
            }).then(() => {
              this.applySwatchInExclusiveComponent(externalFunctionParams);
              this.updateSwatchesData(externalFunctionParams, applyUndo);
              this.updateRecipeWithMerchandisingSwatchRules({ swatch, componentName: currentComponent });
              this.verifyDecoStich(swatch, currentComponent);
              this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'geostates', status: false });
            });

        }
      } else if (swatch.upgrade_options) {
        if (!applyUndo) {
          this.savePreviousSwatch({ feature: 'selfRemovable',  currentSwatch: swatch });
        }
        this.applyUpgradeOptions({ upgradeOptions: swatch.upgrade_options, swatchId: swatch.id }).then(() => {
          this.applySwatchInExclusiveComponent(externalFunctionParams);
          this.updateSwatchesData(externalFunctionParams, applyUndo);
          this.updateRecipeWithMerchandisingSwatchRules({ swatch, componentName: currentComponent })
          this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'swatches', status: false });
        });
      } else {
        const configGroup = this.validateComponentGrouping({ componentName: currentComponent });

        const color = swatch.subgroup === "standard-ugc-solids" ? swatch.color : null;
        this.applySwatch(currentComponent, swatch.id, configGroup, color)
          .then(() => {
            if (swatchGroup === 'graphics' && !isLoadingGraphicFromTab) {
              this.$eventBus.$emit('navigationActivateUpdatedFlag', false);
            }

            this.applySwatchInExclusiveComponent(externalFunctionParams);
            this.updateSwatchesData(externalFunctionParams, applyUndo);
            this.verifyDecoStich(swatch, currentComponent);
          });
      }
    },
    updateSwatchesData({ currentComponent, swatch, group, subgroup, ugcswatchgroup }, applyUndo = false) {
      return new Promise((resolve) => {
        const { id, personalization, geometry_updates } = swatch;
        const data = { swatchId: id.toString(), group, subgroup, ugcswatchgroup, geometry_updates };

        if (geometry_updates && geometry_updates.includes(decoStitch) && !this.isDecoStichApplied) {
          const component = this.recipe_v2.components[currentComponent];

          data.swatchId = component.swatch_id.toString();
          data.group = component.group;
          data.subgroup = component.subgroup;
          data.geometry_updates = component.geometry_updates;
          data.swatch_group = component.ugcswatchgroup;
        }

        this.getSwatchRelationBySwatchId({ currentComponent, swatchId: data.swatchId })
          .then(() => {
            if (!this.isApparel) {
              this.updateSwatchData({ applyUndo, currentComponent, data, group, subgroup, swatch });
            } else {
              this.updateSwatchDataForApparel({ currentComponent, data, group, swatch });
            }

            if (personalization) {
              this.$eventBus.$emit('apply-personalization-from-swatch', personalization);
            }
            resolve();
          });
      });
    },
    updateSwatchData({ applyUndo = false, currentComponent, data, group, subgroup, swatch }) {
      if (!applyUndo || this.tempData?.applyUGCComponents.length > 1) {
        this.updateSwatchesStore(currentComponent, data);
      } else {
        this.setSwatchSelectedBySwatch(
          { component: currentComponent, group, subgroup, swatch, swatch_id: Number(swatch.id) },
        );
      }

      if (this.isFlyOutMenuEnable) {
        this.updateStandardMenu(currentComponent);
      } else {
        this.updateLegacyMenu(currentComponent);
      }
    },
    async updateSwatchDataForApparel({ currentComponent, data, group, swatch }) {
      const { color, id, image_assets, material_options } = swatch;
      // TODO Does the Subcomponent need to validate the relation?
      if (data.edited === undefined || data.edited === false) {
        data.edited = true;
      }

      if (color) {
        data.hex = color;
        this.updateSwatchesStore(currentComponent, data);

        const component = currentComponent.split('/');
        const menu = cloneDeep(this.$store.state.standardNavigation.menu);
        if (component.length > 1) {
          const componentName = component[0]
          const subComponentName = component[1];
          const transparent = (id === this.$store.state.gdm.config[0].global.transparent_swatch_id.toString()) || false;
          this.$store.commit("graphicsController/SET_PLACEMENT_SETTING", {
            placementComponent: componentName,
            componentSetting: { id: subComponentName, swatch_id: Number(id), hex: color, transparent: transparent },
            type: "user_defined",
          });
          const currentNode = menu.find(node => node.id === componentName);
          if (currentNode?.children) {
            const child = currentNode.children.find(item => item.id === subComponentName);
            if (child) {
              child.isChecked = true;
            }
          }
          this.$store.commit("standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION", { menu });
        } else {
          if (data.group !== 'graphics') {
            const currentNode = menu.find(node => node.id === currentComponent);
            currentNode.isChecked = true;
            this.$store.commit("standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION", { menu });
          }
        }
      }
      else if (group === 'patterns') {
        this.updateSwatchesStore(currentComponent, data);

        // Transform pattern to base64 to store it selectedSwatches, and add to graphic placment setting
        const component = currentComponent.split('/');
        const menu = cloneDeep(this.$store.state.standardNavigation.menu);
        const currentNode = menu.find(node => node.id === component[0]);
        if (currentNode?.children) {
          const child = currentNode.children.find(item => item.id === component[1]);
          if (child) child.isChecked = true;
        }
        this.$store.commit("standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION", { menu });
        await fetchAndInsertImageToGraphicSettings(
          {
            src: `${
              this.$store.state.endPoints.customizer.s3SwatchesBucket}assets${
                image_assets?.svg_map
                ?? material_options?.textures?.map.image_url
              }`,
            componentID: component[0],
            subcomponentID: component[1],
            swatch_id: id,
          }
        );
      }
    },
    updateSwatchesStore(currentComponent, data) {
      if (!this.productOptionToggle.hasOwnProperty(data.swatchId) &&
        !this.productOptionToggle.hasOwnProperty(currentComponent)) {
        this.$store.commit('swatches/SET_SWATCHES_SELECTED', { component: currentComponent, data });
      }
    },
    verifyDecoStich(swatch, component) {
      if (this.isDecoStichApplied) {
        this.applyDecoStitch(swatch, component);
      } else if (swatch.apply_decostitch) {
        const { componentToHide } = this.pdm.components.decoStitch?.options || {};

        if (componentToHide) {
          const swatch = this.$store.getters["swatches/returnSwatchbyMaterialType"]({ materialType: decoStitch });
          if(swatch) {
            this.applyDecoStitch(swatch, componentToHide);
          }
        }
      }

      this.$eventBus.$emit("ugc-is-removed", {swatch, componentIsUGC: this.$recipe.getComponentAllowedDecostitch() === component, activeComponentIsUGC: this.activeComponentIsUGC});
    },
    async removeApparelGraphic() {
      const component = this.activeComponent;
      const menu = cloneDeep(this.$store.state.standardNavigation.menu);
      const remainingNodes = menu.filter(node => node.id !== component);
      await this.$store.commit("recipe/REMOVE_COMPONENT", component);
      if (component === 'graphic_sleeve_right') await this.$store.commit("recipe/REMOVE_COMPONENT", 'graphic_sleeve_left');
      this.$eventBus.$emit("remove-texture");
      this.$store.commit("standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION", { menu: remainingNodes });
      this.$store.dispatch("buyPanel/GET_PRICE", { recipe: this.recipe_v2 });
      this.$eventBus.$emit("changeTab", { name: component })
    },
    setSwatchSelected({ component, data }) {
      if (this.isApparel && !data.default) {
        data.edited = true;
      }

      this.$store.commit('swatches/SET_SWATCHES_SELECTED', { component, data });
    },
    setSwatchSelectedBySwatch({ component, group, subgroup, swatch, swatch_id }) {
      let isDefault = this.$store.state.recipe.defaultRecipe
        .components?.[component]?.swatch_id === swatch_id ?? false;
      let swatchGroup = group;
      let swatchSubgroup = subgroup;

      if (group !== 'ugc' && swatch) {
        swatchGroup = this.$store.getters['swatches/returnSwatchGroupName']({ swatch });
        swatchSubgroup = this.$store.getters['swatches/returnSwatchSubgroupName']({ swatch });
      }

      isDefault = (group === 'ugc' && swatch_id === 4) ? false : isDefault;

      if (group !== 'upgradeoptions') {
        this.setSwatchSelected({
          component,
          data: {
            default: isDefault,
            group: swatchGroup,
            subgroup: swatchSubgroup,
            swatchId: swatch_id?.toString() ?? '',
          },
        });
      }
    },
    setSwatchSelectedBySubComponentSwatch({ component, sub_components }) {
      const defaultSubComponents = this.$store.state.gdm
        .data?.find(item => item.component3D === component);

      if (!defaultSubComponents) {
        return;
      }

      Object.keys(sub_components).forEach((subComponent) => {
        const { group, subgroup, swatch_id } = sub_components[subComponent];
        const defaultSubComponent = defaultSubComponents.component
          .find(item => item.id === subComponent);
        const swatch = this.$store.getters['swatches/returnSwatchObj']({ swatchId: swatch_id });
        let isDefault = defaultSubComponent?.swatch_id === swatch_id ?? false;
        let swatchGroup = group;
        let swatchSubgroup = subgroup;

        if (group !== 'ugc' && swatch) {
          swatchGroup = this.$store.getters['swatches/returnSwatchGroupName']({ swatch });
          swatchSubgroup = this.$store.getters['swatches/returnSwatchSubgroupName']({ swatch });
        }

        isDefault = (group === 'ugc' && swatch_id === 11021) ? false : isDefault;

        if (group !== 'upgradeoptions') {
          this.setSwatchSelected({
            component: `${ component }/${ subComponent }`,
            data: {
              default: isDefault,
              group: swatchGroup,
              subgroup: swatchSubgroup,
              swatchId: swatch_id?.toString() ?? '',
            },
          });
        }
      });
    },
    /**
     * Set the recipe swatches into the swatches selected store for tabs seleccion
     * @param {*} recipe
     * @returns
     */
    setSwatchesSelectedFromRecipe(recipe) {
      if (!recipe.components) {
        return;
      }

      Object.keys(recipe.components).forEach((component) => {
        let { group, subgroup, sub_components, swatch_id } = recipe.components[component];
        const swatch = this.$store.getters['swatches/returnSwatchObj']({ swatchId: swatch_id });

        this.setSwatchSelectedBySwatch({ component, group, subgroup, swatch, swatch_id });

        if (this.isApparel && sub_components) {
          this.setSwatchSelectedBySubComponentSwatch({ component, sub_components });
        }
      });

      if (this.isFlyOutMenuEnable) {
        this.updateStandardMenu();
      } else {
        this.updateLegacyMenu();
      }
    },
    updateStandardMenu(currentComponent = null) {
      const { selectedSwatches } = this.$store.state.swatches;
      const menu = cloneDeep(this.$store.state.standardNavigation.menu);

      if (!currentComponent) {
        Object.keys(this.recipe_v2.components).forEach((component) => {
          const currentNode = menu.find(node => node.id === component);

          if (currentNode) {
            currentNode.isChecked = !selectedSwatches[component]?.default;
          }
        });
      } else {
        const currentNode = menu.find(node => node.id === currentComponent);

        if (currentNode) {
          currentNode.isChecked = !selectedSwatches[currentComponent]?.default;
        }
      }

      this.$store.commit('standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION', { menu });
    },
    updateLegacyMenu(currentComponent = null) {
      const { selectedSwatches } = this.$store.state.swatches;
      const menu = Object.values(this.$store.state.legacyNavigation.menu);

      if (!currentComponent) {
        Object.keys(this.recipe_v2.components).forEach((component) => {
          const currentNode = menu.find(node => node.name === component);
          if (selectedSwatches[component] && currentNode) {
            this.$store.commit(
              "legacyNavigation/UPDATE_MENU_ITEM_STATE",
              { componentName: component, itemState: !selectedSwatches[component]?.default }
            );
          }
        })
      } else {
        this.$store.commit(
          "legacyNavigation/UPDATE_MENU_ITEM_STATE",
          { componentName: currentComponent, itemState: !selectedSwatches[currentComponent]?.default }
        );
      }
    },
  }
};